import { Box, Button, Flex, Heading, Image, List, ListItem, Stack, Text, createIcon } from "@chakra-ui/react";
import type { ChakraProps } from "@chakra-ui/react";
import { Container } from "components";
import { useGoogleOptimizeExperimentId } from "contexts/DataContext/hooks";
import { useLanguage } from "contexts/LocalizationContext";
import { useGoogleOptimize } from "hooks/useGoogleOptimize";
import NextLink from "next/link";
import { getPageUrl } from "utils/getPageUrl";
import { trackCustomEvent, trackUAEvent } from "utils/tracking";
import { configHowItWorksABCTest } from "./HowItWorksABCTest.config";
import type { HowItWorksSectionCardProps, HowItWorksSectionProps } from "./HowItWorksABCTest.d";

const Line = createIcon({
  displayName: "CheckmarkIcon",
  viewBox: "0 0 316 26",
  path: (
    <svg width="316" height="26" viewBox="0 0 316 26" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0.5 25C91.8043 -8.5257 210.5 -5.43703 315.5 25" stroke="currentColor" strokeDasharray="2 2" />
    </svg>
  ),
});

const WIDTH = { lg: "calc((100vw - 32px - 120px) / 3)", xl: "316px" };
const LINE_WIDTH = { base: "120px", lg: "calc((100vw - 32px - 120px ) / 3)", xl: "316px" };

const HowItWorksSectionCard = ({
  heading,
  content,
  idx,
  imageSrc,
  commonLineStyles,
}: HowItWorksSectionCardProps & { idx: number; commonLineStyles: ChakraProps["sx"] }) => (
  <Box pos="relative">
    <ListItem
      w={WIDTH}
      display="flex"
      justifyContent="space-between"
      alignItems={{ base: "flex-start", lg: "center" }}
      flexDirection={{ base: "row", lg: "column" }}
    >
      <Image
        zIndex={1}
        overflow="hidden"
        src={imageSrc}
        minH={{ base: "auto", lg: 32 }}
        maxH={{ base: 16, lg: 32 }}
        minW={{ base: 16, lg: 32 }}
        mb={{ lg: 14 }}
        order={{ base: 1, lg: 0 }}
        ml={{ base: 8, lg: "unset" }}
        alt={heading}
      />
      <Box textAlign={{ base: "left", lg: "center" }}>
        <Heading variant="h3" as="h3" mb={4}>
          {heading}
        </Heading>
        <Text fontSize={{ base: "md" }}>{content}</Text>
      </Box>
    </ListItem>
    {idx % 2 ? (
      <Line
        sx={commonLineStyles}
        top={{ base: "80%", md: "100%", lg: "20%" }}
        right={{ base: `calc(${LINE_WIDTH.base} / -10)`, lg: "unset" }}
        transform={{ base: "rotate(270deg)", lg: "translateX(62%) rotate(180deg)" }}
      />
    ) : (
      <Line
        sx={commonLineStyles}
        top={{ base: "80%", md: "100%", lg: "10%" }}
        right={{ base: `calc(${LINE_WIDTH.base} / -3.2)`, lg: "unset" }}
        transform={{ base: "rotate(90deg)", lg: "translateX(62%)" }}
      />
    )}
  </Box>
);
const HowItWorksSectionCards = ({ cards }: Pick<HowItWorksSectionProps, "cards">) => (
  <List>
    <Stack direction={{ base: "column", lg: "row" }} spacing={{ base: 8, md: 14, lg: 16 }}>
      {cards.map(({ content, heading, imageSrc }, idx) => {
        const isLastItem = cards.length - 1 === idx;

        const commonLineStyles = {
          color: "greyscale.500",
          w: LINE_WIDTH,
          h: 6,
          pos: "absolute",
          display: isLastItem && "none",
        } as ChakraProps["sx"];

        return (
          <HowItWorksSectionCard
            key={heading}
            commonLineStyles={commonLineStyles}
            content={content}
            heading={heading}
            idx={idx}
            imageSrc={imageSrc}
          />
        );
      })}
    </Stack>
  </List>
);

const CTA_EVENT_UA = ["clicked", "HowItWorks", "CheckYourCompensation"] as const;
const CTA_EVENT = {
  event: "check_compensation",
  label: "check_your_compensation_cta",
  placement: "how_it_works_section",
} as const;

const HowItWorksSection = ({ heading, topHeading, cards, withCTA, ctaText }: HowItWorksSectionProps) => {
  const language = useLanguage();

  const handletrackEventsCta = () => {
    trackUAEvent(CTA_EVENT_UA);
    trackCustomEvent(CTA_EVENT);
  };
  return (
    <Container>
      <Flex py={{ base: 12, lg: 20 }} px={{ base: 4, lg: 16 }} flexDir="column" align={{ lg: "center" }}>
        <Flex flexDir="column" align={{ base: "left", lg: "center" }} mb={{ base: 8, lg: 16 }}>
          <Text fontSize="xs" fontWeight="bold" letterSpacing="1.5px" color="primary.500" mb={{ base: 8, lg: 6 }}>
            {topHeading}
          </Text>
          <Heading as="h2" variant="h2">
            {heading}
          </Heading>
        </Flex>
        <HowItWorksSectionCards cards={cards} />
        {withCTA && (
          <Button
            href={getPageUrl(language, "webappNewClaim")}
            onClick={handletrackEventsCta}
            as={NextLink}
            size="lg"
            h="60px"
            mt={{ base: 12, lg: 14 }}
            variant="primary"
          >
            {ctaText}
          </Button>
        )}
      </Flex>
    </Container>
  );
};

const EXPERIMENT_NAME = "how-it-works-abc-test";

export const HowItWorksABCTest = () => {
  const experimentId = useGoogleOptimizeExperimentId(EXPERIMENT_NAME, [
    { language: "en-nz" },
    { language: "en" },
    { language: "en-gb" },
    { language: "en-ie" },
    { language: "en-ca" },
    { language: "en-au" },
    { language: "en-in" },
  ]);

  const experimentVariant = useGoogleOptimize(experimentId);

  if (experimentVariant === "1" || experimentVariant === "2") {
    return {
      1: <HowItWorksSection {...configHowItWorksABCTest} withCTA />,
      2: <HowItWorksSection {...configHowItWorksABCTest} />,
    }[experimentVariant];
  }

  return null;
};
